import { useMemo } from 'react';
import { Bell, AlertTriangle, AlertOctagon, Info, RefreshCw, CheckCircle } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatCard } from '@/components/data-display/StatCard';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { HelpButton } from '@/features/help';
import { Can } from '@/components/auth/Can';
import { ROUTES, UNE_PERM } from '@/constants';
import { useAlerts } from '@/features/alerts/hooks';
import { AlertSeverity } from '@/types/ops.types';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Operaciones', path: ROUTES.ALERTS },
  { label: 'Alertas', path: ROUTES.ALERTS },
];

const SEV_CFG: Record<string, { variant: 'danger' | 'warning' | 'info' | 'default'; label: string }> = {
  [AlertSeverity.CRITICAL]: { variant: 'danger', label: 'Critica' },
  [AlertSeverity.HIGH]: { variant: 'warning', label: 'Alta' },
  [AlertSeverity.MEDIUM]: { variant: 'info', label: 'Media' },
  [AlertSeverity.LOW]: { variant: 'default', label: 'Baja' },
};

const FILTERS = [
  { value: null, label: 'Todas' },
  { value: AlertSeverity.CRITICAL, label: 'Criticas' },
  { value: AlertSeverity.HIGH, label: 'Altas' },
  { value: AlertSeverity.MEDIUM, label: 'Medias' },
];

export function AlertsPage() {
  const { alerts, allAlerts, isLoading, isError, refetch, acknowledge, isAcknowledging, severityFilter, setSeverityFilter } = useAlerts();

  const stats = useMemo(() => ({
    total: allAlerts.filter((a) => !a.acknowledgedAt).length,
    critical: allAlerts.filter((a) => a.severity === AlertSeverity.CRITICAL && !a.acknowledgedAt).length,
    high: allAlerts.filter((a) => a.severity === AlertSeverity.HIGH && !a.acknowledgedAt).length,
    pending: allAlerts.filter((a) => !a.acknowledgedAt).length,
  }), [allAlerts]);

  if (isLoading) return (<div><PageHeader title="Alertas" breadcrumbs={BREADCRUMBS} /><div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={`s-${i.toString()}`} variant="rectangular" height={80} />)}</div></div>);
  if (isError) return (<div><PageHeader title="Alertas" breadcrumbs={BREADCRUMBS} /><div className="flex flex-col items-center rounded-lg border border-border bg-card px-6 py-16"><AlertTriangle size={48} className="mb-4 text-muted-foreground" /><h3 className="text-lg font-semibold">Error al cargar alertas</h3><Button className="mt-4" onClick={() => refetch()}><RefreshCw size={16} /> Reintentar</Button></div></div>);

  return (
    <div>
      <PageHeader title="Alertas" subtitle="Alertas operativas del sistema" breadcrumbs={BREADCRUMBS} actions={<div className="flex items-center gap-2"><HelpButton pageKey="alerts" /><Button onClick={() => refetch()}><RefreshCw size={16} /> Actualizar</Button></div>} />
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard title="Total Activas" value={stats.total} icon={Bell} />
          <StatCard title="Criticas" value={stats.critical} icon={AlertOctagon} />
          <StatCard title="Altas" value={stats.high} icon={AlertTriangle} />
          <StatCard title="Pendientes" value={stats.pending} icon={Info} />
        </div>

        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <Button key={f.label} variant={severityFilter === f.value ? 'primary' : 'outline'} size="sm" onClick={() => setSeverityFilter(f.value)}>
              {f.label}
            </Button>
          ))}
        </div>

        <div className="space-y-3">
          {alerts.length === 0 && <div className="rounded-lg border border-border bg-card px-6 py-12 text-center text-sm text-muted-foreground">Sin alertas{severityFilter ? ` de tipo ${severityFilter}` : ''}</div>}
          {alerts.map((alert) => {
            const cfg = SEV_CFG[alert.severity] ?? { variant: 'default' as const, label: 'Baja' };
            const isAcked = !!alert.acknowledgedAt;
            return (
              <Card key={alert.id} className={isAcked ? 'opacity-60' : ''}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={cfg.variant}>{cfg.label}</Badge>
                      <span className="text-xs text-muted-foreground">{alert.type}</span>
                    </div>
                    <h3 className="font-semibold text-foreground">{alert.title}</h3>
                    <p className="text-sm text-muted-foreground">{alert.description}</p>
                    <p className="text-xs text-muted-foreground">{new Date(alert.createdAt).toLocaleString('es-MX')}</p>
                    {isAcked && <p className="text-xs text-emerald-600">Revisado por {alert.acknowledgedBy} el {new Date(alert.acknowledgedAt as string).toLocaleString('es-MX')}</p>}
                  </div>
                  {!isAcked && (
                    <Can permission={UNE_PERM.ALERT_ACK}>
                      <Button size="sm" variant="outline" onClick={() => acknowledge(alert.id)} disabled={isAcknowledging}>
                        <CheckCircle size={14} /> Marcar Revisado
                      </Button>
                    </Can>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
